import React, { useEffect, useMemo, useState } from 'react';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { Department, StaffMember, Shift, Schedule, AdminUser, AssignmentStatus, WeeklyMetrics } from '../../types';
import { getWeekDates, formatDayAndDate, formatDateRange, formatDateYYYYMMDD } from '../../utils/dateUtils';
import { Icons } from '../ui/Icons';
import { toast } from 'react-hot-toast';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

export interface PDFPreviewData {
    departments: Department[];
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    weekStartDate: Date; 
    weeklyMetrics?: WeeklyMetrics;
}

interface PDFPreviewPageProps extends PDFPreviewData {
    onDone: () => void;
}

function oklchToRgba(cssText: string): string {
    return cssText.replace(/oklch\(\s*([\d.]+%?)(?:\s+|,\s*)([\d.]+%?)(?:\s+|,\s*)([\d.-]+(?:deg|rad|grad|turn)?%?)(?:\s*(?:\/|,)\s*([\d.]+%?))?\s*\)/gi, (match, p1, p2, p3, p4) => {
        try {
            const L = p1.endsWith('%') ? parseFloat(p1) / 100 : parseFloat(p1);
            const C = p2.endsWith('%') ? (parseFloat(p2) / 100) * 0.4 : parseFloat(p2);
            
            let H = 0;
            const h_str = p3;
            if (h_str.endsWith('deg')) {
                H = parseFloat(h_str.slice(0, -3));
            } else if (h_str.endsWith('rad')) {
                H = (parseFloat(h_str.slice(0, -3)) * 180) / Math.PI;
            } else if (h_str.endsWith('turn')) {
                H = parseFloat(h_str.slice(0, -4)) * 360;
            } else if (h_str.endsWith('grad')) {
                H = parseFloat(h_str.slice(0, -4)) * 0.9;
            } else if (h_str.endsWith('%')) {
                H = (parseFloat(h_str) / 100) * 360;
            } else {
                H = parseFloat(h_str);
            }

            const alpha = p4 ? (p4.endsWith('%') ? parseFloat(p4) / 100 : parseFloat(p4)) : 1;

            const hRad = (H * Math.PI) / 180;
            const ok_a = C * Math.cos(hRad);
            const ok_b = C * Math.sin(hRad);

            // Convert Oklab to LMS
            const l_ = L + 0.3963377774 * ok_a + 0.2158037573 * ok_b;
            const m_ = L - 0.1055613458 * ok_a - 0.0638541728 * ok_b;
            const s_ = L - 0.0894841775 * ok_a - 1.291485548 * ok_b;

            // Reconstruct non-linear LMS
            const lm = l_ * l_ * l_;
            const mm = m_ * m_ * m_;
            const sm = s_ * s_ * s_;

            // LMS to Linear sRGB
            const rLinear = +4.0767416621 * lm - 3.3077115913 * mm + 0.2309699292 * sm;
            const gLinear = -1.2684380046 * lm + 2.6097574011 * mm - 0.3413193965 * sm;
            const bLinear = -0.0041960063 * lm - 0.7034186147 * mm + 1.707614701 * sm;

            // Linear sRGB to standard sRGB
            const toSRGB = (c: number) => {
                return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
            };

            const r = Math.round(Math.max(0, Math.min(1, toSRGB(rLinear))) * 255);
            const g = Math.round(Math.max(0, Math.min(1, toSRGB(gLinear))) * 255);
            const b = Math.round(Math.max(0, Math.min(1, toSRGB(bLinear))) * 255);

            if (alpha !== 1) {
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }
            return `rgb(${r}, ${g}, ${b})`;
        } catch (err) {
            console.error('Failed to convert oklch:', match, err);
            return '#cccccc';
        }
    });
}

function oklabToRgba(cssText: string): string {
    return cssText.replace(/oklab\(\s*([\d.]+%?)(?:\s+|,\s*)([\d.-]+%?)(?:\s+|,\s*)([\d.-]+%?)(?:\s*(?:\/|,)\s*([\d.]+%?))?\s*\)/gi, (match, p1, p2, p3, p4) => {
        try {
            const L = p1.endsWith('%') ? parseFloat(p1) / 100 : parseFloat(p1);
            const ok_a = p2.endsWith('%') ? (parseFloat(p2) / 100) * 0.4 : parseFloat(p2);
            const ok_b = p3.endsWith('%') ? (parseFloat(p3) / 100) * 0.4 : parseFloat(p3);
            const alpha = p4 ? (p4.endsWith('%') ? parseFloat(p4) / 100 : parseFloat(p4)) : 1;

            // Convert Oklab to LMS
            const l_ = L + 0.3963377774 * ok_a + 0.2158037573 * ok_b;
            const m_ = L - 0.1055613458 * ok_a - 0.0638541728 * ok_b;
            const s_ = L - 0.0894841775 * ok_a - 1.291485548 * ok_b;

            // Reconstruct non-linear LMS
            const lm = l_ * l_ * l_;
            const mm = m_ * m_ * m_;
            const sm = s_ * s_ * s_;

            // LMS to Linear sRGB
            const rLinear = +4.0767416621 * lm - 3.3077115913 * mm + 0.2309699292 * sm;
            const gLinear = -1.2684380046 * lm + 2.6097574011 * mm - 0.3413193965 * sm;
            const bLinear = -0.0041960063 * lm - 0.7034186147 * mm + 1.707614701 * sm;

            // Linear sRGB to standard sRGB
            const toSRGB = (c: number) => {
                return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
            };

            const r = Math.round(Math.max(0, Math.min(1, toSRGB(rLinear))) * 255);
            const g = Math.round(Math.max(0, Math.min(1, toSRGB(gLinear))) * 255);
            const b = Math.round(Math.max(0, Math.min(1, toSRGB(bLinear))) * 255);

            if (alpha !== 1) {
                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
            }
            return `rgb(${r}, ${g}, ${b})`;
        } catch (err) {
            console.error('Failed to convert oklab:', match, err);
            return '#cccccc';
        }
    });
}

function sanitizeStylesheetsForPDF(): () => void {
    const originalStates: { sheet: CSSStyleSheet; disabled: boolean }[] = [];
    const tempStyleElements: HTMLStyleElement[] = [];
    const detachedStyles: { node: Element; parent: Node; nextSibling: Node | null }[] = [];

    try {
        const sheets = Array.from(document.styleSheets) as CSSStyleSheet[];
        for (const sheet of sheets) {
            try {
                originalStates.push({ sheet, disabled: sheet.disabled });

                let rules: CSSRule[] = [];
                try {
                    rules = Array.from(sheet.cssRules || sheet.rules || []);
                } catch (ruleError) {
                    continue;
                }

                if (rules.length === 0) continue;

                let cssText = '';
                for (const rule of rules) {
                    cssText += rule.cssText + '\n';
                }

                const lowerCss = cssText.toLowerCase();
                if (lowerCss.includes('oklch') || lowerCss.includes('oklab')) {
                    let sanitizedCss = oklchToRgba(cssText);
                    sanitizedCss = oklabToRgba(sanitizedCss);
                    // Catch-all regex to strip any remaining modern colors as absolute backup
                    sanitizedCss = sanitizedCss.replace(/oklab\([^)]*\)/gi, 'rgb(0,0,0)');
                    sanitizedCss = sanitizedCss.replace(/oklch\([^)]*\)/gi, 'rgb(0,0,0)');

                    // Disable original
                    sheet.disabled = true;

                    // Dynamically detach the style node from head or parent.
                    // This physically removes it from document.styleSheets so html2canvas doesn't process it.
                    const owner = sheet.ownerNode;
                    if (owner && owner instanceof Element) {
                        const parent = owner.parentNode;
                        if (parent) {
                            detachedStyles.push({
                                node: owner,
                                parent: parent,
                                nextSibling: owner.nextSibling
                            });
                            parent.removeChild(owner);
                        }
                    }

                    // Create safe replacement stylesheet
                    const tempStyle = document.createElement('style');
                    tempStyle.type = 'text/css';
                    tempStyle.setAttribute('data-pdf-safe-style', 'true');
                    tempStyle.appendChild(document.createTextNode(sanitizedCss));
                    document.head.appendChild(tempStyle);

                    tempStyleElements.push(tempStyle);
                }
            } catch (err) {
                console.error('Error in sanitizeStylesheetsForPDF:', err);
            }
        }
    } catch (gErr) {
        console.error('Failed to iterate over stylesheets:', gErr);
    }

    return () => {
        // Restore original stylesheet states
        for (const state of originalStates) {
            try {
                state.sheet.disabled = state.disabled;
            } catch (e) {
                console.error('Failed to restore stylesheet disabled state:', e);
            }
        }

        // Re-attach detached original style nodes
        for (const detached of detachedStyles) {
            try {
                detached.parent.insertBefore(detached.node, detached.nextSibling);
            } catch (e) {
                console.error('Failed to re-attach stylesheet node:', e);
            }
        }

        // Remove temporary safe stylesheets
        for (const tempStyle of tempStyleElements) {
            try {
                tempStyle.remove();
            } catch (e) {
                console.error('Failed to remove temp stylesheet:', e);
            }
        }
    };
}

interface StylesBackup {
    element: HTMLElement;
    originalInlineStyles: { [key: string]: string };
}

function applyRGBInlineStyles(container: HTMLElement): () => void {
    const backupList: StylesBackup[] = [];
    const colorProps = [
        'color',
        'background-color',
        'border-color',
        'border-top-color',
        'border-bottom-color',
        'border-left-color',
        'border-right-color',
        'fill',
        'stroke'
    ];

    function traverse(node: Element) {
        if (node && (node as any).style && typeof (node as any).style.setProperty === 'function') {
            const htmlNode = node as HTMLElement;
            try {
                const win = htmlNode.ownerDocument?.defaultView || window;
                const computed = win.getComputedStyle ? win.getComputedStyle(htmlNode) : window.getComputedStyle(htmlNode);
                const backup: { [key: string]: string } = {};
                let hasChange = false;

                for (const prop of colorProps) {
                    try {
                        const val = computed.getPropertyValue(prop);
                        if (typeof val === 'string' && (val.includes('oklch') || val.includes('oklab'))) {
                            // Back up original inline style value
                            backup[prop] = htmlNode.style.getPropertyValue(prop);
                            
                            // Convert color functions found in computed styles
                            let converted = oklabToRgba(oklchToRgba(val));
                            if (converted.includes('oklch') || converted.includes('oklab')) {
                                converted = converted.replace(/oklab\([^)]*\)/gi, 'rgb(0,0,0)');
                                converted = converted.replace(/oklch\([^)]*\)/gi, 'rgb(0,0,0)');
                            }

                            // Apply standard RGB/RGBA or hex value as inline style (no computed priority is needed)
                            htmlNode.style.setProperty(prop, converted);
                            hasChange = true;
                        }
                    } catch (innerErr) {
                        // Skip any individual failed properties
                    }
                }

                if (hasChange) {
                    backupList.push({ element: htmlNode, originalInlineStyles: backup });
                }
            } catch (err) {
                // Ignore errors finding computed style for specific elements
            }
        }

        const children = node.children;
        if (children) {
            for (let i = 0; i < children.length; i++) {
                traverse(children[i]);
            }
        }
    }

    traverse(container);

    return () => {
        // Restore step
        for (const backup of backupList) {
            for (const [prop, originalVal] of Object.entries(backup.originalInlineStyles)) {
                try {
                    if (originalVal === '') {
                        backup.element.style.removeProperty(prop);
                    } else {
                        backup.element.style.setProperty(prop, originalVal);
                    }
                } catch (e) {
                    try {
                        (backup.element.style as any)[prop] = originalVal;
                    } catch (err) {
                        // Ignore restoration edge cases
                    }
                }
            }
        }
    };
}

const PDFPreviewPage: React.FC<PDFPreviewPageProps> = ({ 
    departments, 
    people, 
    shifts, 
    schedule, 
    weekStartDate, 
    weeklyMetrics = {}, 
    onDone 
}) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadedBlobUrl, setDownloadedBlobUrl] = useState<string | null>(null);
    const [pdfFilename, setPdfFilename] = useState<string>('');

    const weekDates = useMemo(() => getWeekDates(weekStartDate), [weekStartDate]);
    const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));

    const staffWeeklyHours = useMemo(() => {
        const hours: { [staffId: string]: number } = {};
        for (const person of people) {
            let personHours = 0;
            for (const date of weekDates) {
                const dateString = formatDateYYYYMMDD(date);
                const entry = schedule[person.id]?.[dateString];
                if (entry?.status === 'ASSIGNED' && entry.shiftId) {
                    const shift = shiftsMap.get(entry.shiftId);
                    if (shift) {
                        const start = new Date(`1970-01-01T${shift.startTime}:00`);
                        const end = new Date(`1970-01-01T${shift.endTime}:00`);
                        if (end < start) {
                            end.setDate(end.getDate() + 1);
                        }
                        personHours += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                    }
                }
            }
            hours[person.id] = personHours;
        }
        return hours;
    }, [schedule, people, shiftsMap, weekDates]);

    const dailyDepartmentHours = useMemo(() => {
        const hours: { [date: string]: { [depId: string]: number } } = {};
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            hours[dateString] = {};
            departments.forEach(dep => hours[dateString][dep.id] = 0);
        });

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule && person.departmentId) {
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED' && entry.shiftId) {
                        const shift = shiftsMap.get(entry.shiftId);
                        if (shift) {
                            const start = new Date(`1970-01-01T${shift.startTime}:00`);
                            const end = new Date(`1970-01-01T${shift.endTime}:00`);
                            if (end < start) end.setDate(end.getDate() + 1);
                            hours[dateString][person.departmentId!] += (end.getTime() - start.getTime()) / (1000 * 60 * 60);
                        }
                    }
                });
            }
        });
        return hours;
    }, [schedule, people, departments, weekDates, shiftsMap]);

    const dailyStaffCount = useMemo(() => {
        const counts: { [date: string]: { [depId: string]: number } } = {};
        
        weekDates.forEach(date => {
            const dateString = formatDateYYYYMMDD(date);
            counts[dateString] = {};
            departments.forEach(dep => {
                counts[dateString][dep.id] = 0;
            });
        });

        people.forEach(person => {
            const personSchedule = schedule[person.id];
            if (personSchedule && person.departmentId) {
                const depId = person.departmentId;
                weekDates.forEach(date => {
                    const dateString = formatDateYYYYMMDD(date);
                    const entry = personSchedule[dateString];
                    if (entry && entry.status === 'ASSIGNED') {
                        const dateCounts = counts[dateString];
                        if (dateCounts && typeof dateCounts[depId] === 'number') {
                            dateCounts[depId]++;
                        }
                    }
                });
            }
        });

        return counts;
    }, [schedule, people, departments, weekDates]);

    const departmentWeeklyHours = useMemo(() => {
        const hours: { [depId: string]: number } = {};
        departments.forEach(dep => hours[dep.id] = 0);
        people.forEach(person => {
            if (person.departmentId) {
                hours[person.departmentId] += staffWeeklyHours[person.id] || 0;
            }
        });
        return hours;
    }, [departments, people, staffWeeklyHours]);

    const departmentStaffMap = useMemo(() => {
        const map = new Map<string, SchedulablePerson[]>();
        departments.forEach(dep => map.set(dep.id, []));
        people.forEach(person => {
            if (person.departmentId && map.has(person.departmentId)) {
                map.get(person.departmentId)?.push(person);
            }
        });
        return map;
    }, [departments, people]);

    const printableRows = useMemo(() => {
        const rows: {
            type: 'dept' | 'staff';
            department: Department;
            person?: SchedulablePerson;
        }[] = [];

        departments.forEach(dep => {
            const depStaff = departmentStaffMap.get(dep.id) || [];
            if (depStaff.length === 0) return;

            rows.push({
                type: 'dept',
                department: dep
            });

            depStaff.forEach(person => {
                rows.push({
                    type: 'staff',
                    department: dep,
                    person
                });
            });
        });

        return rows;
    }, [departments, departmentStaffMap]);

    const pdfPagesData = useMemo(() => {
        const totalStaff = people.length;
        const isLargeSchedule = totalStaff > 15;
        const isVeryLargeSchedule = totalStaff > 30;

        const baseRowHeight = isVeryLargeSchedule ? 30 : isLargeSchedule ? 38 : 46;
        const maxPageHeight = 580; 

        const pages: (typeof printableRows)[] = [];
        let currentTempPage: typeof printableRows = [];
        let currentPageHeight = 0;

        printableRows.forEach(row => {
            const estRowHeight = row.type === 'dept' ? baseRowHeight * 0.8 : baseRowHeight;
            if (currentTempPage.length > 0 && currentPageHeight + estRowHeight > maxPageHeight) {
                pages.push(currentTempPage);
                currentTempPage = [];
                currentPageHeight = 0;
            }
            currentTempPage.push(row);
            currentPageHeight += estRowHeight;
        });

        if (currentTempPage.length > 0) {
            pages.push(currentTempPage);
        }

        if (pages.length === 0) {
            pages.push([]);
        }

        return pages;
    }, [printableRows, people]);

    // Clean up blob url on unmount
    useEffect(() => {
        return () => {
            if (downloadedBlobUrl) {
                URL.revokeObjectURL(downloadedBlobUrl);
            }
        };
    }, [downloadedBlobUrl]);

    // Action: Trigger browser standard layout landscape native print
    const handlePrintClick = () => {
        const originalBodyStyle = document.body.style.cssText;
        document.body.style.cssText = 'background-color: white;';
        
        const tempContainer = document.createElement('div');
        tempContainer.id = 'print-temp-rendering-block';
        tempContainer.style.cssText = 'position: absolute; left: 0; top: 0; width: 100%; z-index: 999999; background: white;';

        const sourceEl = document.getElementById('preview-pdf-pages-blueprint');
        if (sourceEl) {
            tempContainer.innerHTML = sourceEl.innerHTML;
            document.body.appendChild(tempContainer);
            
            // Hide the app main container temporarily for print focus
            const rootEl = document.getElementById('root');
            if (rootEl) rootEl.style.display = 'none';

            const timer = setTimeout(() => {
                window.print();
                
                // Cleanup and restore
                if (rootEl) rootEl.style.display = '';
                tempContainer.remove();
                document.body.style.cssText = originalBodyStyle;
            }, 250);
        } else {
            toast.error("Internal Error: Preview template could not be verified.");
            document.body.style.cssText = originalBodyStyle;
        }
    };

    // Action: Compile via html2pdf & attempt direct download (with fallback Blob creation)
    const handleDownloadClick = () => {
        console.log("PDF Generation Triggered: Initiating custom pre-processing sequence.");
        const element = document.getElementById('preview-pdf-pages-blueprint');
        if (!element) {
            console.error("PDF Generation Aborted: Target element '#preview-pdf-pages-blueprint' was not found in the DOM.");
            toast.error("Could not process schedule pages element.");
            return;
        }

        setIsDownloading(true);
        console.log("PDF Generation Step 1: Temporarily sanitizing OKLCH and OKLAB stylesheet declarations.");
        const restoreStylesheets = sanitizeStylesheetsForPDF();
        
        console.log("PDF Generation Step 2: Recursively injecting converted RGB/RGBA inline styles on container DOM branches.");
        const restoreInline = applyRGBInlineStyles(element);
        
        const filename = `schedule-${formatDateRange(weekStartDate).replace(/\s+/g, '_')}.pdf`;
        setPdfFilename(filename);
        console.log(`PDF Generation Step 3: Configured destination filename correctly: "${filename}"`);

        // Prevent layout shift/clipping by temporarily overriding max-width and scroll constraints during generation
        const originalMaxWidth = element.style.maxWidth;
        const originalOverflowX = element.style.overflowX;
        element.style.maxWidth = 'none';
        element.style.overflowX = 'visible';

        const opt = {
            margin:       0,
            filename:     filename,
            image:        { type: 'jpeg' as const, quality: 0.98 },
            html2canvas:  { 
                scale: 2, 
                useCORS: true, 
                logging: false,
                width: 1056,
                windowWidth: 1056,
                scrollX: 0,
                scrollY: 0,
                x: 0,
                y: 0
            },
            jsPDF:        { unit: 'px' as const, format: [1056, 816] as [number, number], orientation: 'landscape' as const },
            pagebreak:    { mode: ['css', 'legacy'] }
        };

        try {
            console.log("PDF Generation Step 4: Loading html2pdf rendering library instance.");
            let html2pdfInstance = (window as any).html2pdf || html2pdf;
            if (html2pdfInstance && typeof html2pdfInstance !== 'function' && (html2pdfInstance as any).default) {
                html2pdfInstance = (html2pdfInstance as any).default;
            }

            if (!html2pdfInstance || typeof html2pdfInstance !== 'function') {
                throw new Error("html2pdf library load failure - constructor is not a valid function.");
            }

            const isInIframe = window.self !== window.top;
            console.log(`PDF Generation Step 5: Rendering context identified. Browser iframe sandbox active: ${isInIframe}`);

            if (!isInIframe) {
                console.log("PDF Generation Step 6A: Not inside sandboxed iframe workspace view. Triggering standard native .save() direct stream.");
                html2pdfInstance()
                    .from(element)
                    .set(opt)
                    .save()
                    .then(() => {
                        console.log("PDF Generation Complete: Standard native download finished successfully.");
                        setIsDownloading(false);
                        toast.success("PDF exported successfully!");
                     })
                    .catch((err: any) => {
                        console.error("PDF Generation Failure in .save() stage:", err);
                        toast.error("Direct download failed. Please try saving using the 'Save as PDF' print option.");
                        setIsDownloading(false);
                    })
                    .finally(() => {
                        console.log("PDF Generation Step 7A: Restoring original document styles and active classes.");
                        element.style.maxWidth = originalMaxWidth;
                        element.style.overflowX = originalOverflowX;
                        restoreStylesheets();
                        restoreInline();
                    });
            } else {
                console.log("PDF Generation Step 6B: Inside sandbox iframe. Bypassing direct save browser blocks by rendering safely to Blob.");
                html2pdfInstance()
                    .from(element)
                    .set(opt)
                    .output('blob')
                    .then((blob: Blob) => {
                        console.log("PDF Generation Step 7B: successfully converted layout tree to binary container. Creating download reference URL.");
                        const blobUrl = URL.createObjectURL(blob);
                        setDownloadedBlobUrl(blobUrl);
                        setIsDownloading(false);
                        toast.success("PDF successfully compiled and ready for download!");
                    })
                    .catch((err: any) => {
                        console.error("PDF compiling to blob failed:", err);
                        toast.error("Compilation failed. Use print option below to export perfectly.");
                        setIsDownloading(false);
                    })
                    .finally(() => {
                        console.log("PDF Generation Step 8B: Restoring original document styles and active classes.");
                        element.style.maxWidth = originalMaxWidth;
                        element.style.overflowX = originalOverflowX;
                        restoreStylesheets();
                        restoreInline();
                    });
            }
        } catch (err: any) {
            console.error("PDF export initialization crash during rendering chain orchestration:", err);
            toast.error("Standard PDF engine loader error. Try using 'Save as PDF via Print'.");
            element.style.maxWidth = originalMaxWidth;
            element.style.overflowX = originalOverflowX;
            restoreStylesheets();
            restoreInline();
            setIsDownloading(false);
        }
    };

    const totalStaff = people.length;
    const isLargeSchedule = totalStaff > 15;
    const isVeryLargeSchedule = totalStaff > 30;

    const cellStyle: React.CSSProperties = {
        paddingTop: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
        paddingBottom: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
        paddingLeft: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
        paddingRight: isVeryLargeSchedule ? '4px' : isLargeSchedule ? '6px' : '8px',
        lineHeight: isVeryLargeSchedule ? '1.2' : isLargeSchedule ? '1.3' : '1.4',
        verticalAlign: 'top',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        border: '1px solid #cbd5e1',
        fontSize: isVeryLargeSchedule ? '8px' : isLargeSchedule ? '9px' : '10px'
    };

    const headerCellStyle: React.CSSProperties = {
        ...cellStyle,
        backgroundColor: '#f1f5f9',
        color: '#334155',
        fontWeight: 'bold'
    };

    return (
        <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col antialiased">
            <style type="text/css" media="print">
                {`
                    @page { size: landscape; margin: 0; }
                    body { background: white !important; color: black !important; -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
                    #preview-pdf-pages-blueprint { display: block !important; }
                    .pdf-page { margin: 0 !important; box-shadow: none !important; border: none !important; page-break-after: always; break-after: page; }
                `}
            </style>

            {/* Top Navigation Control bar */}
            <header className="sticky top-0 z-50 bg-slate-800/95 border-b border-slate-700/80 backdrop-blur-md px-4 py-3 sm:px-6 shadow-md flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onDone}
                        className="inline-flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-slate-100 dark:hover:bg-slate-700/50 transition-all border border-slate-700 hover:border-slate-600 bg-slate-800"
                        title="Back to Schedule Editor"
                    >
                        <Icons.ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-base font-bold text-slate-50 tracking-tight">PDF Export Studio</h1>
                        <p className="text-xs text-slate-400 font-medium">{formatDateRange(weekStartDate)}</p>
                    </div>
                </div>

                <div className="flex items-center flex-wrap gap-2.5">
                    {/* Standard automated compilation & download */}
                    <button
                        onClick={handleDownloadClick}
                        disabled={isDownloading}
                        className="flex items-center gap-1.5 px-5 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800/80 text-white rounded-lg shadow-md transition-all border border-emerald-500 disabled:border-slate-700 disabled:opacity-50"
                    >
                        {isDownloading ? (
                            <Icons.Loader className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Icons.Download className="w-3.5 h-3.5" />
                        )}
                        <span>{isDownloading ? 'Compiling PDF...' : 'Download PDF Document'}</span>
                    </button>
                </div>
            </header>

            {/* Notice banner when compiled with Blob instructions (crucial for iframe sandbox workaround) */}
            {downloadedBlobUrl && (
                <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-4 py-3 text-center sm:px-6">
                    <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-emerald-400">
                        <div className="flex items-center gap-2 text-left">
                            <Icons.Check className="w-4 h-4 flex-shrink-0" />
                            <p className="text-xs leading-relaxed text-slate-200">
                                <strong className="text-emerald-400">Download Ready!</strong> Due to your browser's sandboxed iframe security policies for development preview, direct background download was redirected securely. Click on the right button to download:
                            </p>
                        </div>
                        <a
                            href={downloadedBlobUrl}
                            download={pdfFilename}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all shadow-md shrink-0 uppercase tracking-wider"
                        >
                            <Icons.Download className="w-3.5 h-3.5" />
                            <span>Save PDF File</span>
                        </a>
                    </div>
                </div>
            )}

            {/* Main Interactive Studio Canvas Page */}
            <main className="flex-1 overflow-auto p-6 flex flex-col items-center gap-8 self-center max-w-full">
                <div className="text-center max-w-xl space-y-1">
                    <h2 className="text-lg font-bold text-slate-200">Interactive PDF Document Layout</h2>
                    <p className="text-xs text-slate-400 leading-relaxed">
                        Below is a preview of the landscape PDF document sheets. Use the button above to compile and download your schedule.
                    </p>
                </div>

                {/* PDF Container Wrapper */}
                <div 
                    id="preview-pdf-pages-blueprint" 
                    className="flex flex-col gap-6"
                    style={{ width: '1056px', maxWidth: '100%', overflowX: 'auto', backgroundColor: 'transparent' }}
                >
                    {pdfPagesData.map((pageRows, pageIndex) => {
                        const pageNum = pageIndex + 1;
                        const totalPages = pdfPagesData.length;

                        return (
                            <React.Fragment key={`pdf-page-${pageIndex}`}>
                                <div 
                                    className="pdf-page bg-white text-slate-900 relative mx-auto shadow-2xl rounded-sm border border-slate-700/20" 
                                    style={{ 
                                        width: '1056px', 
                                        minHeight: '816px', 
                                        padding: '38px 38px 38px 60px', 
                                        boxSizing: 'border-box', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        justifyContent: 'space-between',
                                        backgroundColor: '#ffffff'
                                    }}
                                >
                                    <div>
                                        {/* Page Header (Title, Range) */}
                                        <div className="flex items-center justify-between pb-2 mb-3" style={{ borderBottom: '1.5px solid #cbd5e1' }}>
                                            <div>
                                                <h1 className="text-sm font-black tracking-tight uppercase" style={{ color: '#0f172a' }}>Weekly Schedule Report</h1>
                                                <p className="text-[8px] font-bold" style={{ color: '#64748b' }}>
                                                    Scheduler Pro Platform {totalPages > 1 && `| Page ${pageNum} of ${totalPages}`}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <h2 className="text-xs font-bold" style={{ color: '#1e293b' }}>{formatDateRange(weekStartDate)}</h2>
                                                <p className="text-[8px] font-semibold mt-0.5" style={{ color: '#64748b' }}>
                                                    Generated On: <span className="font-bold">{new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                </p>
                                            </div>
                                        </div>

                                        {/* Table Grid with exact same responsive styling */}
                                        <table className="w-full border-collapse" style={{ border: '1px solid #cbd5e1', tableLayout: 'auto', width: '958px' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#f1f5f9', color: '#334155' }}>
                                                    <th style={{ ...headerCellStyle, width: '16%' }} className="text-left uppercase tracking-wider">
                                                        Staff Member
                                                    </th>
                                                    {weekDates.map((date) => {
                                                        const { day, date: dateNum } = formatDayAndDate(date);
                                                        return (
                                                            <th key={`${date.toISOString()}-pdf-hdr-${pageIndex}`} style={{ ...headerCellStyle, backgroundColor: '#f8fafc', width: '11%' }} className="text-center">
                                                                <div className="font-bold text-[10px]" style={{ color: '#1e293b' }}>{dateNum}</div>
                                                                <div className="text-[8px] font-bold uppercase tracking-wide" style={{ color: '#64748b' }}>{day}</div>
                                                            </th>
                                                        );
                                                    })}
                                                    <th style={{ ...headerCellStyle, backgroundColor: '#f8fafc', width: '7%' }} className="text-center uppercase tracking-wider">
                                                        Hours
                                                    </th>
                                                </tr>
                                                <tr style={{ backgroundColor: '#fffbeb', color: '#b45309' }}>
                                                    <td style={{ ...cellStyle, fontWeight: 'bold', backgroundColor: '#fef3c7', color: '#92400e', borderBottom: '1.5px solid #cbd5e1' }} className="text-left text-[10px] font-bold uppercase tracking-wider">
                                                        Occupancy
                                                    </td>
                                                    {weekDates.map((date) => {
                                                        const dateString = formatDateYYYYMMDD(date);
                                                        const occ = weeklyMetrics[dateString]?.occupancy || 0;
                                                        return (
                                                            <td key={`${date.toISOString()}-pdf-hdr-occ-${pageIndex}`} style={{ ...cellStyle, backgroundColor: '#fffbeb', fontWeight: 'bold', color: '#b45309', borderBottom: '1.5px solid #cbd5e1' }} className="text-center text-[10px]">
                                                                {occ}%
                                                            </td>
                                                        );
                                                    })}
                                                    <td style={{ ...cellStyle, backgroundColor: '#fef3c7', borderBottom: '1.5px solid #cbd5e1' }} className="text-center text-[10px]"></td>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {pageRows.map((row, rowIndex) => {
                                                    if (row.type === 'dept') {
                                                        return (
                                                            <tr key={`pdf-page-${pageIndex}-dept-${rowIndex}`} style={{ backgroundColor: '#f1f5f9', color: '#334155', fontWeight: 'bold' }}>
                                                                <td style={{ ...cellStyle, borderLeft: `3px solid ${row.department.color}`, fontWeight: 'bold' }}>
                                                                    <span style={{ color: row.department.color }}>{row.department.name}</span>
                                                                </td>
                                                                {weekDates.map(date => {
                                                                    const dateString = formatDateYYYYMMDD(date);
                                                                    return (
                                                                        <td key={`${dateString}-pdf-dep-tot-${pageIndex}-${rowIndex}`} style={cellStyle} className="text-center">
                                                                            <div className="font-semibold leading-none">{dailyStaffCount[dateString]?.[row.department.id] || 0} Staff</div>
                                                                            <div className="text-[7px] mt-0.5" style={{ color: '#94a3b8' }}>{(dailyDepartmentHours[dateString]?.[row.department.id] || 0).toFixed(1)}h</div>
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td style={{ ...cellStyle, backgroundColor: '#f1f5f9', fontWeight: 'bold', color: '#334155' }} className="text-center">
                                                                    {departmentWeeklyHours[row.department.id]?.toFixed(1)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    } else if (row.type === 'staff' && row.person) {
                                                        const person = row.person;
                                                        return (
                                                            <tr key={`pdf-page-${pageIndex}-staff-${person.id}`} style={{ backgroundColor: '#ffffff' }}>
                                                                <td style={{ ...cellStyle, borderLeft: `3px solid ${person.color || row.department.color}`, fontWeight: 'bold', color: '#1e293b' }}>
                                                                    <div className="font-bold text-gray-950">{person.name}</div>
                                                                    <div className="text-[8px] font-normal leading-tight mt-1" style={{ color: '#64748b' }}>{person.role}</div>
                                                                </td>
                                                                {weekDates.map(date => {
                                                                    const dateString = formatDateYYYYMMDD(date);
                                                                    const entry = schedule[person.id]?.[dateString];
                                                                    let content: React.ReactNode = <span style={{ color: '#cbd5e1' }}>-</span>;
                                                                    let bgColor = 'transparent';
                                                                    let borderLeftVal = 'none';

                                                                    if (entry) {
                                                                         if (entry.status === 'ASSIGNED' && entry.shiftId) {
                                                                             const shift = shifts.find(s => s.id === entry.shiftId);
                                                                             if (shift) {
                                                                                 content = (
                                                                                     <div className="leading-tight">
                                                                                         <div className="font-bold text-[9px] text-gray-900">{shift.name}</div>
                                                                                         <div className="text-[8px] font-medium mt-1 text-gray-500">{shift.startTime}-{shift.endTime}</div>
                                                                                     </div>
                                                                                 );
                                                                                 const isHex = shift.color?.startsWith('#');
                                                                                 bgColor = isHex ? `${shift.color}15` : 'rgba(99, 102, 241, 0.08)';
                                                                                 borderLeftVal = `2px solid ${shift.color || '#cbd5e1'}`;
                                                                             }
                                                                         } else {
                                                                              const labels: { [key: string]: string } = {
                                                                                  OFF: 'OFF',
                                                                                  SICK: 'SICK',
                                                                                  VACATION: 'VAC',
                                                                                  ON_CALL: 'CALL'
                                                                              };
                                                                              content = <span className="font-bold text-[8px] tracking-wider" style={{ color: '#475569' }}>{labels[entry.status] || '-'}</span>;
                                                                              if (entry.status === 'OFF') {
                                                                                  bgColor = '#f3f4f6';
                                                                              } else if (entry.status === 'VACATION') {
                                                                                  bgColor = '#dbeafe';
                                                                              } else if (entry.status === 'SICK') {
                                                                                  bgColor = '#fef9c3';
                                                                              } else if (entry.status === 'ON_CALL') {
                                                                                  bgColor = '#e0f2fe';
                                                                              }
                                                                         }
                                                                    }

                                                                    return (
                                                                        <td
                                                                            key={`${dateString}-pdf-cell-${pageIndex}`}
                                                                            style={{ ...cellStyle, backgroundColor: bgColor, borderLeft: borderLeftVal }}
                                                                            className="text-center align-top"
                                                                        >
                                                                            {content}
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td style={{ ...cellStyle, color: '#334155', backgroundColor: '#f8fafc', fontWeight: 'bold' }} className="text-center">
                                                                    {staffWeeklyHours[person.id]?.toFixed(1)}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Page Footer Disclaimer */}
                                    <div className="flex items-center justify-between mt-3 pt-2" style={{ borderTop: '1px solid #e1e7f0' }}>
                                        <div className="text-[7.5px] font-bold tracking-wide uppercase" style={{ color: '#64748b' }}>
                                            Configured Departments Report: {departments.map(d => d.name).join(', ')}
                                        </div>
                                        <div className="text-[7.5px] font-extrabold tracking-widest text-[#6366f1] uppercase">
                                            Scheduler Pro Document Solutions &copy; 2026
                                        </div>
                                    </div>
                                </div>
                                {pageNum < totalPages && <div className="html2pdf__page-break" />}
                            </React.Fragment>
                        );
                    })}
                </div>
            </main>
        </div>
    );
};

export default PDFPreviewPage;
